// src/utils/payAttemptLog.js
//
// NZSC-4126: PG 결제창 시도·결과 기록 (api-public `POST /api/v1/pay-attempt`, `/pay-attempt/result`).
//
// 결제창을 열 때마다 1행(open), 결과가 오면 갱신(result). 결과가 끝까지 없으면 "창 열고 사라짐".
// 호출부(IMP.request_pay 10여 곳·PaypleCpayAuthCheck 10여 곳)를 건드리지 않고 pgResultProbe 의
// SDK 전역 래퍼가 이 모듈을 부른다 — 결제 흐름·금액·UI 는 그대로다.
//
// 안전 규칙(필수):
//  - fire-and-forget: await 하지 않는다. 모든 경로 try/catch. 결제창 열기를 지연·차단하지 않는다.
//  - api.js 인스턴스를 쓰지 않는다 — 401 시 logout·reload, errorContext(api) 덮어쓰기 부작용이 있다.
//    behaviorQueue 와 같은 keepalive fetch(모바일은 open 직후 PG 로 페이지가 넘어가도 완주)만 쓴다.
//  - 실패해도 Sentry/Slack/alert 없음. 비로그인(토큰 없음)이면 보내지 않는다(AuthGuard 401 소음 방지).
//
// 모바일 리다이렉트(콜백 없음): 창 열기 직전 sessionStorage 에 {order_no→attempt_key} 를 남기고,
// 앱 부팅 시 consumeRedirectLanding() 이 착지 URL 에서 결과를 읽어 보낸다.
// ★pending 키·형식은 web-core(NZSC-4126)와 공용 계약이다 — web-core 가 연 결제창(이용권 충전 등)의
//   착지가 Vue 페이지(/ticket/price-complete)라서 한쪽만 바꾸면 결과가 유실된다.
//   키 `nz_pay_attempt_pending`, 값 `{ [order_no]: { k, pg, product, t } }`, 24h 만료, 최대 20건(오래된 것부터 삭제).

export const PAY_ATTEMPT_ENDPOINTS = {
  OPEN: "/api/v1/pay-attempt",
  RESULT: "/api/v1/pay-attempt/result",
};

export const PENDING_KEY = "nz_pay_attempt_pending"; // web-core 공용 계약
const PENDING_TTL_MS = 24 * 60 * 60 * 1000; // 공용 계약
const PENDING_CAP = 20; // 공용 계약
// 주문번호 없이 오는 착지(성공 전용 착지·셜록 취소)는 "가장 최근 시도"로 잇는다 — 오귀속 방지를 위해 짧게
const NO_ID_LANDING_MAX_AGE_MS = 30 * 60 * 1000;
const SENT_KEY = "nz_pay_attempt_sent"; // Vue 전용 — 같은 결과 중복 전송 방지(콜백+착지, 새로고침)
const SENT_CAP = 30;
const TOKEN_KEY = "naezip_to_ac_se";

/** product 고정 목록(snake_case). 서버·홍시 조회와 같은 값이어야 한다 */
export const PAY_PRODUCTS = [
  "report",
  "ticket",
  "subscribe",
  "guarantee",
  "sherlock",
  "wolse_pay",
  "owner_property",
  "alfred",
  "insurance_fa",
  "insurance_lotte",
  "other",
];

// 프론트 경로 → product (앞에서부터 첫 매치)
const PATH_RULES = [
  [/^\/(doby|pay-order)(\/|$)/, "wolse_pay"],
  [/^\/sherlock(\/|$)/, "sherlock"],
  [/^\/(ticket\/|analyze\/ticket-complete)/, "ticket"],
  [/^\/subscribe(\/|$)/, "subscribe"],
  [/^\/guarantee(\/|$)/, "guarantee"],
  [/^\/consult(\/|$)/, "alfred"],
  [/^\/insurance\/firstamerican(\/|$)/, "insurance_fa"],
  [/^\/insurance\/lotte(\/|$)/, "insurance_lotte"],
  [/^\/(owner\/|report\/owner)/, "owner_property"],
  [/^\/(analyze|amount|report)(\/|$)/, "report"],
];

// 페이플 모바일 PCD_RST_URL(api-public 서버 리다이렉트) → product. 범용 order-redirect/complete/payple 은 판정 불가(null)
const SERVER_HINT_RULES = [
  [/\/order-redirect\/ticket\//, "ticket"],
  [/\/order-redirect\/expiration\//, "report"],
  [/\/payple\/payment\//, "subscribe"],
  [/\/subscribe-payment\//, "subscribe"],
  [/\/pay-order-redirect\//, "wolse_pay"],
];

// 주문번호 접두 → product (api-public order-redirect.controller 분기에서 확인된 것만)
const ORDER_PREFIX_RULES = [
  [/^shr/, "sherlock"],
  [/^15/, "owner_property"],
  [/^18/, "insurance_fa"],
];

// 페이플 모바일 서버 리다이렉트가 **성공일 때만** 보내는 착지(Vue 서빙). 주문번호가 쿼리에 없는 착지 포함
const PAYPLE_SUCCESS_LANDINGS = [
  /^\/analyze\/pay-complete$/,
  /^\/analyze\/ticket-complete$/,
  /^\/analyze\/complete$/,
  /^\/report\/owner-house\/pay-complete$/,
  /^\/report\/expiration\/pay-complete$/,
  /^\/report\/buy-premium-upgrade\/complete$/,
  /^\/insurance\/payment\/complete$/,
  /^\/sherlock\/payment\/complete$/,
  /^\/ticket\/price-complete$/,
  /^\/pay-order\/complete$/,
  /^\/subscribe\/complete$/,
];

const clip = (v, n) => {
  if (v === undefined || v === null || v === "") return undefined;
  const s = String(v);
  return s.length > n ? s.slice(0, n) : s;
};

const win = () => (typeof window !== "undefined" ? window : null);

const store = () => {
  try {
    const w = win();
    return w && w.sessionStorage ? w.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

function readJson(key, fallback) {
  try {
    const st = store();
    const raw = st ? st.getItem(key) : null;
    const v = raw ? JSON.parse(raw) : null;
    if (Array.isArray(fallback)) return Array.isArray(v) ? v : fallback;
    return v && typeof v === "object" && !Array.isArray(v) ? v : fallback;
  } catch (e) {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    const st = store();
    if (st) st.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* 저장 실패가 결제를 막아선 안 된다 */
  }
}

export function newAttemptKey() {
  try {
    const c = win() && win().crypto;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch (e) {
    /* 폴백 */
  }
  // RFC4122 v4 형식 폴백
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0;
    return (ch === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function hasStoredAppVersion() {
  // 앱이 보낸 app_device_info 가 persist 된 userStore._deviceInfo.app_version (app-update-modal 과 같은 판별)
  try {
    const raw = win().localStorage.getItem("vuex");
    const v = raw ? JSON.parse(raw) : null;
    const ver = v && v.userStore && v.userStore._deviceInfo && v.userStore._deviceInfo.app_version;
    return typeof ver === "string" && ver.length > 0;
  } catch (e) {
    return false;
  }
}

export function detectDevice() {
  try {
    const w = win();
    // 내집스캔 앱(Flutter InAppWebView) — store/index.js·safeOpenUrl 과 같은 판별 + persist 된 앱 버전(web-core 와 동일 규칙)
    if (w && (w.flutter_inappwebview || hasStoredAppVersion())) return "app";
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    // statusCheck.isMobile 과 같은 기준
    if (/iPhone|Android|iPad|iPod/.test(ua)) return "mobile";
  } catch (e) {
    /* noop */
  }
  return "pc";
}

function pathOf(url) {
  try {
    if (!url) return null;
    const w = win();
    const base = (w && w.location && w.location.origin) || "https://www.naezipscan.com";
    return new URL(String(url), base).pathname;
  } catch (e) {
    return null;
  }
}

const matchRules = (rules, s) => {
  if (!s) return null;
  for (const [re, product] of rules) if (re.test(s)) return product;
  return null;
};

/**
 * product 판정 — ①결과 착지 URL(m_redirect_url / PCD_RST_URL) ②현재 경로 ③주문번호 접두 ④other.
 * 착지 URL 을 먼저 보는 이유: 같은 결제 버튼 컴포넌트가 여러 화면에 쓰인다(예: 이용권 충전 모달이
 * 리포트 화면에 뜸). 착지 URL 은 호출부가 퍼널별로 고정해 두므로 가장 구체적인 힌트다.
 */
export function resolveProduct({ hintUrl, pathname, orderNo } = {}) {
  try {
    const hintPath = pathOf(hintUrl);
    if (hintPath) {
      const p = /^\/api\//.test(hintPath) ? matchRules(SERVER_HINT_RULES, hintPath) : matchRules(PATH_RULES, hintPath);
      if (p) return p;
    }
    const byPath = matchRules(PATH_RULES, pathname);
    if (byPath) return byPath;
    const byOrder = matchRules(ORDER_PREFIX_RULES, orderNo ? String(orderNo) : "");
    if (byOrder) return byOrder;
  } catch (e) {
    /* noop */
  }
  return "other";
}

function apiBase() {
  // api.js resolveApiHost 와 같은 규칙(MRI 프리뷰 빌드만 override). api.js 를 import 하지 않는 이유는 상단 주석.
  try {
    if (process.env.VUE_APP_MRI_BRIDGE === "1") {
      const o = win().localStorage.getItem("mri_api_host");
      if (o && /^https:\/\//.test(o)) return o.replace(/\/$/, "");
    }
  } catch (e) {
    /* 기본값 */
  }
  return (process.env.VUE_APP_HOST || "").replace(/\/$/, "");
}

function stripUndefined(obj) {
  const out = {};
  Object.keys(obj).forEach(k => {
    if (obj[k] !== undefined) out[k] = obj[k];
  });
  return out;
}

/** fire-and-forget 전송. 절대 throw 하지 않고, 반환값을 기다리는 곳도 없다 */
function post(endpoint, body) {
  try {
    const w = win();
    if (!w || typeof w.fetch !== "function") return;
    let token = null;
    try {
      token = w.localStorage ? w.localStorage.getItem(TOKEN_KEY) : null;
    } catch (e) {
      token = null;
    }
    if (!token) return;
    const p = w.fetch(apiBase() + endpoint, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(stripUndefined(body)),
    });
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch (e) {
    /* 기록 실패는 조용히 버린다 */
  }
}

/** 만료 제거 후 [order_no, entry] 배열(오래된 순) */
function pendingEntries() {
  const now = Date.now();
  const map = readJson(PENDING_KEY, {});
  return Object.keys(map)
    .map(o => [o, map[o]])
    .filter(([, e]) => e && typeof e.t === "number" && now - e.t < PENDING_TTL_MS)
    .sort((a, b) => a[1].t - b[1].t);
}

function writePending(entries) {
  const out = {};
  entries.slice(-PENDING_CAP).forEach(([o, e]) => {
    out[o] = e;
  });
  writeJson(PENDING_KEY, out);
}

/** 주문번호가 있을 때만 남긴다(착지 페이지는 주문번호로만 찾을 수 있다). r=콜백 없는 리다이렉트 모드(Vue 확장 필드) */
function savePending(orderNo, entry) {
  if (!orderNo) return;
  const entries = pendingEntries().filter(([o]) => o !== orderNo);
  entries.push([orderNo, entry]);
  writePending(entries);
}

/** 조건에 맞는 가장 최근 항목을 꺼내고 지운다 → [order_no, entry] | null */
function takePending(predicate) {
  const entries = pendingEntries();
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    if (predicate(entries[i][0], entries[i][1])) {
      const found = entries[i];
      entries.splice(i, 1);
      writePending(entries);
      return found;
    }
  }
  return null;
}

function wasSent(ids) {
  const sent = readJson(SENT_KEY, []);
  return ids.some(id => id && sent.indexOf(String(id)) >= 0);
}

function markSent(ids) {
  const sent = readJson(SENT_KEY, []);
  ids.forEach(id => {
    if (id && sent.indexOf(String(id)) < 0) sent.push(String(id));
  });
  writeJson(SENT_KEY, sent.slice(-SENT_CAP));
}

/**
 * 결제창 열기 기록. attempt_key 를 반환한다(콜백 결과를 같은 행에 잇기 위해).
 * redirect=true(모바일·콜백 없음)면 착지 페이지가 찾을 수 있게 sessionStorage 에 남긴다.
 */
export function recordPayAttemptOpen({ pg, payMethod, amount, orderNo, hintUrl, redirect } = {}) {
  let attemptKey = null;
  try {
    attemptKey = newAttemptKey();
    const w = win();
    const pathname = (w && w.location && w.location.pathname) || undefined;
    const product = resolveProduct({ hintUrl, pathname, orderNo });
    const amt = Number(amount);
    const order = clip(orderNo, 100);
    const entry = { k: attemptKey, pg, product, t: Date.now() };
    if (redirect) entry.r = true;
    savePending(order, entry);
    post(PAY_ATTEMPT_ENDPOINTS.OPEN, {
      attempt_key: attemptKey,
      pg,
      product,
      pay_method: clip(payMethod, 30), // DTO MaxLength(30)
      page_path: clip(pathname, 255),
      // DTO @IsInt @Min(0) — 어긋나면 400 으로 open 행 전체가 유실되므로 필드만 뺀다
      amount: amount !== undefined && amount !== null && amount !== "" && Number.isInteger(amt) && amt >= 0 ? amt : undefined,
      order_no: order,
      device: detectDevice(),
    });
  } catch (e) {
    /* noop */
  }
  return attemptKey;
}

/** 결과 기록. 같은 시도를 두 번 보내지 않는다(콜백+착지 중복, 새로고침) */
export function recordPayAttemptResult({ attemptKey, orderNo, pg, success, status, resultCode, resultMsg } = {}) {
  try {
    const order = clip(orderNo, 100);
    const pending = takePending((o, e) => (attemptKey && e.k === attemptKey) || (order && o === order && e.pg === pg));
    const key = attemptKey || (pending && pending[1].k) || undefined;
    // 식별자가 하나도 없으면 보내지 않는다 — 서버가 신규 행을 만들어 중복이 된다(web-core 와 같은 규칙)
    if (!key && !order) return;
    const ids = [key, order && `${pg}:${order}`];
    if (wasSent(ids)) return;
    markSent(ids);
    post(PAY_ATTEMPT_ENDPOINTS.RESULT, {
      attempt_key: key,
      order_no: order,
      pg,
      success: typeof success === "boolean" ? success : undefined,
      status: clip(status, 50),
      result_code: clip(resultCode, 100),
      result_msg: clip(resultMsg, 500),
    });
  } catch (e) {
    /* noop */
  }
}

/** PortOne(IMP) 콜백 rsp → 결과 필드 */
export function portoneResult(rsp) {
  const r = rsp || {};
  const s = r.success !== undefined ? r.success : r.imp_success;
  return {
    orderNo: r.merchant_uid,
    success: typeof s === "boolean" ? s : s === "true",
    status: r.status,
    resultCode: r.error_code,
    resultMsg: r.error_msg,
  };
}

/** 페이플 콜백 res → 결과 필드 */
export function paypleResult(res) {
  const r = res || {};
  return {
    orderNo: r.PCD_PAY_OID,
    success: r.PCD_PAY_RST === "success",
    status: r.PCD_PAY_RST,
    resultCode: r.PCD_PAY_CODE,
    resultMsg: r.PCD_PAY_MSG,
  };
}

const parseBool = v => (v === "true" ? true : v === "false" ? false : undefined);

/**
 * 앱 부팅 시 1회 — 모바일 리다이렉트 착지 URL 에서 결과를 읽어 보낸다(풀 페이지 로드 = 부팅).
 *  ①PortOne m_redirect_url: 쿼리 imp_uid/imp_success/merchant_uid/error_code/error_msg
 *  ②페이플 서버 리다이렉트(api-public order-redirect 등): 성공일 때만 주문번호(merchant_uid|mid|uid)를
 *    붙이거나 성공 전용 착지로 보낸다. 취소는 셜록만 payment_cancelled=true. 그 외 실패·닫힘은
 *    /analyze/error·/mypage(web-core)·금액 화면으로 가 주문번호가 없어 여기서 판정하지 않는다.
 */
export function consumeRedirectLanding(loc) {
  try {
    const w = win();
    const l = loc || (w && w.location);
    if (!l || !l.search) return;
    const q = new URLSearchParams(l.search);
    const pathname = l.pathname || "";

    const merchantUid = q.get("merchant_uid");
    if (merchantUid && (q.get("imp_uid") !== null || q.get("imp_success") !== null) && q.get("pg_name") !== "payple") {
      const success = parseBool(q.get("imp_success") !== null ? q.get("imp_success") : q.get("success"));
      recordPayAttemptResult({
        orderNo: merchantUid,
        pg: "portone",
        success,
        resultCode: q.get("error_code") || undefined,
        resultMsg: q.get("error_msg") || undefined,
      });
      return;
    }

    if (q.get("PCD_PAY_RST") !== null) {
      recordPayAttemptResult({ pg: "payple", ...paypleResult(Object.fromEntries(q.entries())) });
      return;
    }

    const now = Date.now();
    const payplePending = pendingEntries().filter(([, e]) => e.pg === "payple");
    if (!payplePending.length) return;
    const ids = ["merchant_uid", "mid", "uid", "PCD_PAY_OID"].map(k => q.get(k)).filter(Boolean);
    const matched = payplePending.filter(([o]) => ids.indexOf(o) >= 0).pop();
    // 주문번호 없는 착지: 이 탭에서 방금(30분 내) 리다이렉트 모드로 연 페이플 시도만 인정
    const recent = payplePending.filter(([, e]) => e.r && now - e.t < NO_ID_LANDING_MAX_AGE_MS).pop();
    const target = matched || (ids.length ? null : recent);
    if (!target) return;

    if (q.get("payment_cancelled") === "true") {
      recordPayAttemptResult({ attemptKey: target[1].k, orderNo: target[0], pg: "payple", success: false, status: "close", resultMsg: `landing:${pathname}` });
      return;
    }
    const isSuccessLanding = PAYPLE_SUCCESS_LANDINGS.some(re => re.test(pathname));
    // 주문번호 없이 온 성공 착지는 착지 경로의 product 가 그 시도의 product 와 같을 때만 인정
    // (고객이 포기한 시도 뒤 같은 탭에서 무관한 완료 화면에 온 경우를 성공으로 오기록하지 않게)
    const productMatches = !!matched || resolveProduct({ pathname }) === target[1].product;
    if (isSuccessLanding && productMatches) {
      // 서버가 성공일 때만 이 착지로 보낸다 — PCD 원문이 없어 result_msg 에 착지 경로를 남겨 추정임을 표시
      recordPayAttemptResult({ attemptKey: target[1].k, orderNo: target[0], pg: "payple", success: true, status: "success", resultMsg: `landing:${pathname}` });
    }
  } catch (e) {
    /* noop */
  }
}
